import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FlaskConical,
  Play,
  CheckCircle,
  XCircle,
  Sparkles,
  Wand2,
  ChevronDown,
  ChevronRight,
  Plus,
  Loader2,
  Copy,
  Check,
  AlertCircle,
} from 'lucide-react';
import {
  useOrderLogicRules,
  useSimulateRules,
  useGeneratePayload,
  useGenerateRule,
} from '../../hooks/useOrderLogicRules';
import PageHeader from '../../components/common/PageHeader';
import type {
  CreateOrderLogicRuleRequest,
  RuleLevel,
  SimulationResultItem,
} from '../../api/orderLogicRules';

// ─── Default sample data ───

const DEFAULT_ORDER_DATA = {
  status: 'completed',
  product: 'urn:oms:Product:mortgage',
  originator: 'urn:originator:encompass',
  requester: 'urn:lender:acme',
  request: {
    payload: {
      number: '2025-LN-00123',
      amount: 350000,
      loanType: 'conventional',
      propertyType: 'single_family',
      propertyState: 'CA',
      borrowerName: 'John Smith',
      borrowerEmail: 'john.smith@example.com',
    },
  },
};

const DEFAULT_USER_DATA = {
  id: 'usr_12345',
  name: 'John Smith',
  email: 'john.smith@example.com',
  groups: ['borrowers'],
  orderHistory: {
    totalOrders: 3,
    lastOrderDate: '2025-12-15T10:30:00Z',
  },
};

const DEFAULT_RESPONSE_DATA = {
  payload: {
    status: 'success',
    trackingId: 'TRK-2025-001',
    context: {
      completedAt: '2026-01-20T14:00:00Z',
      processingTime: 48,
    },
  },
};

// ─── Collapsible Section Component ───

function CollapsibleSection({
  title,
  icon,
  defaultOpen = true,
  children,
  actions,
}: {
  title: string;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between p-3 lg:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2"
        >
          {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          {icon}
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
        </button>
        {actions && <div>{actions}</div>}
      </div>
      {open && <div className="px-3 pb-3 lg:px-4 lg:pb-4">{children}</div>}
    </div>
  );
}

// ─── Condition Result Row ───

function ConditionRow({ result }: { result: SimulationResultItem['conditionResults'][0] }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      {result.passed ? (
        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-mono text-gray-700 dark:text-gray-300 truncate">
          <span className="text-blue-600 dark:text-blue-400">{result.source}</span>.
          <span className="font-semibold">{result.field}</span>{' '}
          <span className="text-purple-600 dark:text-purple-400">{result.operator}</span>{' '}
          <span className="text-amber-600 dark:text-amber-400">{JSON.stringify(result.expectedValue)}</span>
        </p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          Actual: <span className={result.passed ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
            {result.actualValue === undefined ? 'undefined' : JSON.stringify(result.actualValue)}
          </span>
        </p>
      </div>
    </div>
  );
}

// ─── Rule Result Card ───

function RuleResultCard({
  result,
  onGeneratePayload,
  isGeneratingPayload,
  onCreateRule,
}: {
  result: SimulationResultItem;
  onGeneratePayload: (ruleUrn?: string) => void;
  isGeneratingPayload: boolean;
  onCreateRule?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const passedCount = result.conditionResults.filter((c) => c.passed).length;
  const totalCount = result.conditionResults.length;

  return (
    <div
      className={`rounded border transition-colors ${
        result.matched
          ? 'border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/10'
          : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/30'
      }`}
    >
      <div className="p-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {result.matched ? (
              <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-gray-400 shrink-0" />
            )}
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {result.rule.name}
            </span>
            <span className="badge bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 text-[10px] shrink-0">
              {result.rule.trigger.replace(/^on_/, '').replace(/_/g, ' ')}
            </span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0">
              P{result.rule.priority}
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 ml-2">
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {passedCount}/{totalCount}
            </span>
            <button
              onClick={() => onGeneratePayload(result.rule.urn)}
              disabled={isGeneratingPayload}
              className="btn-icon text-xs p-1"
              title="Generate matching payload with AI"
            >
              <Wand2 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="btn-icon text-xs p-1"
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {result.rule.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 ml-7 mt-0.5">{result.rule.description}</p>
        )}

        {/* Expanded details */}
        {expanded && (
          <div className="mt-3 ml-7 space-y-3">
            {/* Condition breakdown */}
            <div>
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 uppercase tracking-wider">
                Conditions
              </p>
              <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {result.conditionResults.map((cr, i) => (
                  <ConditionRow key={i} result={cr} />
                ))}
              </div>
            </div>

            {/* Actions for matched rules */}
            {result.matched && result.matchedActions.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-green-700 dark:text-green-300 mb-1 uppercase tracking-wider">
                  Actions Triggered
                </p>
                {result.matchedActions.map((a, i) => (
                  <div key={i} className="bg-green-100/50 dark:bg-green-900/20 rounded p-2 mt-1">
                    <p className="text-[11px] font-medium text-green-800 dark:text-green-200 mb-1">
                      {a.type}
                    </p>
                    <pre className="text-[10px] text-green-700 dark:text-green-300 font-mono overflow-auto max-h-40">
                      {JSON.stringify(a.config, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Rule button */}
      {onCreateRule && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50/50 dark:bg-gray-800/30">
          <button
            onClick={onCreateRule}
            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium flex items-center gap-1"
          >
            <Plus className="h-3 w-3" /> Create This Rule in System
          </button>
        </div>
      )}
    </div>
  );
}

// ─── AI Generated Rule Preview ───

function AIRulePreview({
  rule,
  onSimulate,
  onCreateRule,
}: {
  rule: Partial<CreateOrderLogicRuleRequest>;
  onSimulate: () => void;
  onCreateRule: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(rule, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="card border-2 border-indigo-200 dark:border-indigo-800 overflow-hidden">
      <div className="px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-500" />
          <span className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
            AI Generated Rule
          </span>
          <span className="badge bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[10px]">
            {rule.name}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={handleCopy} className="btn-icon text-xs p-1" title="Copy JSON">
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Trigger:</span>{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {rule.trigger?.replace(/^on_/, '').replace(/_/g, ' ')}
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Priority:</span>{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{rule.priority}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Lenders:</span>{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {rule.applicableLenders?.length || 'All'}
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Products:</span>{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {rule.applicableProducts?.length || 'All'}
            </span>
          </div>
        </div>
        {rule.description && (
          <p className="text-xs text-gray-600 dark:text-gray-300">{rule.description}</p>
        )}
        <pre className="text-[10px] font-mono bg-gray-100 dark:bg-gray-800 rounded p-3 overflow-auto max-h-48 text-gray-700 dark:text-gray-300">
          {JSON.stringify(rule.rootLevel, null, 2)}
        </pre>
        <div className="flex gap-2">
          <button onClick={onSimulate} className="btn-primary text-xs gap-1">
            <Play className="h-3.5 w-3.5" /> Simulate This Rule
          </button>
          <button onClick={onCreateRule} className="btn-secondary text-xs gap-1">
            <Plus className="h-3.5 w-3.5" /> Create in System
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───

export default function RuleSimulatorPage() {
  const navigate = useNavigate();
  const { data: rules, isLoading: isLoadingRules } = useOrderLogicRules();
  const simulateMutation = useSimulateRules();
  const generatePayloadMutation = useGeneratePayload();
  const generateRuleMutation = useGenerateRule();

  // Sample data state
  const [orderJson, setOrderJson] = useState(JSON.stringify(DEFAULT_ORDER_DATA, null, 2));
  const [userJson, setUserJson] = useState(JSON.stringify(DEFAULT_USER_DATA, null, 2));
  const [responseJson, setResponseJson] = useState(JSON.stringify(DEFAULT_RESPONSE_DATA, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);

  // Simulation results
  const [results, setResults] = useState<SimulationResultItem[] | null>(null);

  // AI Rule Generator
  const [aiDescription, setAiDescription] = useState('');
  const [generatedRule, setGeneratedRule] = useState<Partial<CreateOrderLogicRuleRequest> | null>(null);

  // Navigate to dedicated rule edit page with pre-filled data
  const navigateToCreateRule = (ruleData: Partial<CreateOrderLogicRuleRequest>) => {
    navigate('/rules/order-logic/new', { state: { prefill: ruleData } });
  };

  // ─── Client-side evaluation fallback ───

  const resolveField = (obj: Record<string, unknown>, path: string): unknown =>
    path.split('.').reduce((curr: unknown, key) => {
      if (curr && typeof curr === 'object') return (curr as Record<string, unknown>)[key];
      return undefined;
    }, obj);

  const evaluateConditionLocal = (
    cond: { field: string; operator: string; value: unknown; source: 'order' | 'user' | 'response' },
    data: { order: Record<string, unknown>; user: Record<string, unknown>; response: Record<string, unknown> },
  ): { passed: boolean; actualValue: unknown } => {
    const sourceData = data[cond.source] || {};
    const fieldValue = resolveField(sourceData, cond.field);
    const expected = cond.value;
    let passed = false;
    switch (cond.operator) {
      case 'equals': passed = String(fieldValue) === String(expected); break;
      case 'not_equals': passed = String(fieldValue) !== String(expected); break;
      case 'greater_than': passed = Number(fieldValue) > Number(expected); break;
      case 'less_than': passed = Number(fieldValue) < Number(expected); break;
      case 'greater_than_or_equals': passed = Number(fieldValue) >= Number(expected); break;
      case 'less_than_or_equals': passed = Number(fieldValue) <= Number(expected); break;
      case 'contains': passed = String(fieldValue).includes(String(expected)); break;
      case 'not_contains': passed = !String(fieldValue).includes(String(expected)); break;
      case 'in': passed = Array.isArray(expected) ? expected.includes(fieldValue) : false; break;
      case 'not_in': passed = Array.isArray(expected) ? !expected.includes(fieldValue) : true; break;
      case 'exists': passed = fieldValue !== undefined && fieldValue !== null; break;
      case 'not_exists': passed = fieldValue === undefined || fieldValue === null; break;
      case 'starts_with': passed = String(fieldValue).startsWith(String(expected)); break;
      case 'ends_with': passed = String(fieldValue).endsWith(String(expected)); break;
      case 'between':
        if (Array.isArray(expected) && expected.length === 2) {
          const num = Number(fieldValue);
          passed = num >= Number(expected[0]) && num <= Number(expected[1]);
        }
        break;
      default: passed = false;
    }
    return { passed, actualValue: fieldValue };
  };

  const evaluateLevelLocal = (
    level: RuleLevel,
    data: { order: Record<string, unknown>; user: Record<string, unknown>; response: Record<string, unknown> },
  ): { matched: boolean; conditionResults: SimulationResultItem['conditionResults'] } => {
    const conditionResults: SimulationResultItem['conditionResults'] = [];
    for (const cond of level.conditions) {
      const { passed, actualValue } = evaluateConditionLocal(cond, data);
      conditionResults.push({
        condition: `${cond.source}.${cond.field} ${cond.operator} ${JSON.stringify(cond.value)}`,
        source: cond.source,
        field: cond.field,
        operator: cond.operator,
        expectedValue: cond.value,
        actualValue,
        passed,
      });
    }
    const matched =
      level.operator === 'AND'
        ? conditionResults.length > 0 && conditionResults.every((r) => r.passed)
        : conditionResults.some((r) => r.passed);
    if (!matched && level.elseLevel) {
      const elseResult = evaluateLevelLocal(level.elseLevel, data);
      return { matched: elseResult.matched, conditionResults: [...conditionResults, ...elseResult.conditionResults] };
    }
    return { matched, conditionResults };
  };

  const runClientSimulation = (
    order: Record<string, unknown>,
    user: Record<string, unknown>,
    resp: Record<string, unknown>,
  ): SimulationResultItem[] => {
    const enabledRules = (rules || []).filter((r) => r.enabled);
    const data = { order, user, response: resp };
    return enabledRules
      .sort((a, b) => b.priority - a.priority)
      .map((rule) => {
        const { matched, conditionResults } = evaluateLevelLocal(rule.rootLevel, data);
        return {
          rule: {
            urn: rule.urn,
            name: rule.name,
            trigger: rule.trigger,
            priority: rule.priority,
            description: rule.description,
            enabled: rule.enabled,
          },
          matched,
          conditionResults,
          matchedActions: matched
            ? rule.rootLevel.actions.map((a) => ({ type: a.type, config: a.config }))
            : [],
        };
      });
  };

  // ─── Handlers ───

  const runSimulation = () => {
    try {
      const order = JSON.parse(orderJson);
      const user = JSON.parse(userJson);
      const response = JSON.parse(responseJson);
      setParseError(null);

      // Try backend first, fall back to client-side
      simulateMutation.mutate(
        { orderData: order, userData: user, responseData: response },
        {
          onSuccess: (data) => setResults(data),
          onError: () => {
            // Fallback: run client-side evaluation
            const clientResults = runClientSimulation(order, user, response);
            setResults(clientResults);
          },
        },
      );
    } catch (err) {
      setParseError(`Invalid JSON: ${(err as Error).message}`);
    }
  };

  const handleGeneratePayload = (ruleUrn?: string) => {
    const request = ruleUrn ? { ruleUrn } : generatedRule ? { rule: generatedRule } : null;
    if (!request) return;

    generatePayloadMutation.mutate(request, {
      onSuccess: (data) => {
        setOrderJson(JSON.stringify(data.order, null, 2));
        setUserJson(JSON.stringify(data.user, null, 2));
        setResponseJson(JSON.stringify(data.response, null, 2));
      },
    });
  };

  const handleGenerateRule = () => {
    if (!aiDescription.trim()) return;
    generateRuleMutation.mutate(aiDescription, {
      onSuccess: (data) => setGeneratedRule(data),
    });
  };

  const handleSimulateGeneratedRule = () => {
    // Run simulation with current data - the generated rule needs to exist in the system
    // or we do a client-side evaluation. For now, just trigger normal simulation.
    runSimulation();
  };

  // ─── Grouped Results ───

  const { matchedRules, unmatchedRules } = useMemo(() => {
    if (!results) return { matchedRules: [], unmatchedRules: [] };
    return {
      matchedRules: results.filter((r) => r.matched),
      unmatchedRules: results.filter((r) => !r.matched),
    };
  }, [results]);

  return (
    <div>
      <PageHeader
        title="Rule Simulator"
        description="Test order logic rules against sample data, generate rules with AI, and create new rules"
        actions={
          <button
            onClick={runSimulation}
            disabled={isLoadingRules || simulateMutation.isPending}
            className="btn-primary gap-1"
          >
            {simulateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Run Simulation
          </button>
        }
      />

      {/* AI Rule Generator */}
      <div className="card p-4 lg:p-5 mb-4 lg:mb-6 border-2 border-dashed border-indigo-200 dark:border-indigo-800 bg-indigo-50/30 dark:bg-indigo-900/10">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-indigo-500" />
          <h2 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
            AI Rule Generator
          </h2>
          <span className="text-[10px] badge bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
            Powered by OpenAI
          </span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Describe a rule in plain English and let AI generate the rule definition for you.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            className="input flex-1"
            placeholder='e.g. "When a mortgage order completes with amount over $200k from lender Acme, create a title order"'
            value={aiDescription}
            onChange={(e) => setAiDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleGenerateRule();
            }}
          />
          <button
            onClick={handleGenerateRule}
            disabled={!aiDescription.trim() || generateRuleMutation.isPending}
            className="btn-primary gap-1 shrink-0"
          >
            {generateRuleMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
            Generate Rule
          </button>
        </div>
        {generateRuleMutation.isError && (
          <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Failed to generate rule. Make sure the OPENAI_API_KEY is configured on the backend.
          </p>
        )}
      </div>

      {/* AI Generated Rule Preview */}
      {generatedRule && (
        <div className="mb-4 lg:mb-6">
          <AIRulePreview
            rule={generatedRule}
            onSimulate={handleSimulateGeneratedRule}
            onCreateRule={() => generatedRule && navigateToCreateRule(generatedRule)}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Left: Sample Data */}
        <div className="space-y-3">
          <CollapsibleSection
            title="Order Data"
            icon={<FlaskConical className="h-4 w-4 text-blue-500" />}
            actions={
              <button
                onClick={() => handleGeneratePayload(rules?.[0]?.urn)}
                disabled={generatePayloadMutation.isPending || !rules?.length}
                className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 font-medium flex items-center gap-1"
                title="Auto-generate data with AI"
              >
                {generatePayloadMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Wand2 className="h-3 w-3" />
                )}
                AI Fill
              </button>
            }
          >
            <textarea
              className="input font-mono text-xs h-52 resize-y"
              value={orderJson}
              onChange={(e) => setOrderJson(e.target.value)}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="User Data"
            defaultOpen={true}
          >
            <textarea
              className="input font-mono text-xs h-32 resize-y"
              value={userJson}
              onChange={(e) => setUserJson(e.target.value)}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Response Data"
            defaultOpen={true}
          >
            <textarea
              className="input font-mono text-xs h-32 resize-y"
              value={responseJson}
              onChange={(e) => setResponseJson(e.target.value)}
            />
          </CollapsibleSection>

          {parseError && (
            <div className="flex items-center gap-2 p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">{parseError}</p>
            </div>
          )}

          {generatePayloadMutation.isError && (
            <div className="flex items-center gap-2 p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to generate payload. Check that OPENAI_API_KEY is configured.
              </p>
            </div>
          )}
        </div>

        {/* Right: Simulation Results */}
        <div className="space-y-3">
          {!results && !simulateMutation.isPending ? (
            <div className="card p-8 text-center">
              <FlaskConical className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Configure your sample data and click <strong>"Run Simulation"</strong> to see which rules apply.
              </p>
            </div>
          ) : simulateMutation.isPending ? (
            <div className="card p-8 text-center">
              <Loader2 className="h-8 w-8 text-blue-500 mx-auto mb-3 animate-spin" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Running simulation...</p>
            </div>
          ) : results && results.length === 0 ? (
            <div className="card p-8 text-center">
              <AlertCircle className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No enabled rules found in the system.
              </p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="flex gap-3">
                <div className="flex-1 card p-3 border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-lg font-bold text-green-700 dark:text-green-300">{matchedRules.length}</p>
                      <p className="text-[11px] text-green-600 dark:text-green-400">Rules Apply</p>
                    </div>
                  </div>
                </div>
                <div className="flex-1 card p-3 border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-lg font-bold text-gray-700 dark:text-gray-300">{unmatchedRules.length}</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">Rules Don't Apply</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Matched Rules */}
              {matchedRules.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Rules That Apply ({matchedRules.length})
                  </h3>
                  <div className="space-y-2">
                    {matchedRules.map((result, idx) => (
                      <RuleResultCard
                        key={idx}
                        result={result}
                        onGeneratePayload={handleGeneratePayload}
                        isGeneratingPayload={generatePayloadMutation.isPending}
                        onCreateRule={
                          result.rule.urn
                            ? () => {
                                // Navigate to the rule's edit page
                                navigate(`/rules/order-logic/${encodeURIComponent(result.rule.urn!)}`);
                              }
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched Rules */}
              {unmatchedRules.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <XCircle className="h-3.5 w-3.5" />
                    Rules That Don't Apply ({unmatchedRules.length})
                  </h3>
                  <div className="space-y-2">
                    {unmatchedRules.map((result, idx) => (
                      <RuleResultCard
                        key={idx}
                        result={result}
                        onGeneratePayload={handleGeneratePayload}
                        isGeneratingPayload={generatePayloadMutation.isPending}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

    </div>
  );
}
